import React from 'react';

// String-producing counterpart to parseInlineMarkdown below — converts
// **bold**/_italic_ markers into real <strong>/<em> tags in a plain string,
// for callers that build HTML (e.g. via dangerouslySetInnerHTML) rather than
// React elements, and so can't use parseInlineMarkdown's element output.
export const markdownInlineToHtml = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/_(.+?)_/g, '<em>$1</em>');
};

// Full string-producing markdown → HTML converter: applies markdownInlineToHtml
// (bold/italic) plus block-level "- "/"• " bullet-line grouping into a real
// <ul><li> list, mirroring renderPlainTextWithFormatting's line parsing below
// but returning an HTML string for callers stuck with dangerouslySetInnerHTML
// (e.g. TTS word-highlighting, which can't render React elements).
export const markdownToHtml = (text) => {
  if (!text) return '';
  const textStr = String(text).trim();
  if (!textStr.includes('\n')) {
    return markdownInlineToHtml(textStr);
  }

  const lines = textStr.split('\n');
  const htmlParts = [];
  let listItems = [];

  const flushList = () => {
    if (listItems.length > 0) {
      htmlParts.push(
        `<ul style="margin:8px 0;padding-left:20px;list-style-type:disc;">${listItems
          .map((item) => `<li style="margin:4px 0;">${item}</li>`)
          .join('')}</ul>`
      );
      listItems = [];
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      listItems.push(markdownInlineToHtml(trimmed.slice(2)));
    } else if (trimmed === '') {
      flushList();
    } else {
      flushList();
      htmlParts.push(`<p style="margin:6px 0;line-height:1.5;">${markdownInlineToHtml(trimmed)}</p>`);
    }
  });

  flushList();
  return htmlParts.join('');
};

// Helper to parse bold/italic markdown in a line of text, rendering HTML directly if tags are present
export const parseInlineMarkdown = (line) => {
  if (!line) return '';
  const lineStr = String(line);
  if (/<[a-z][\s\S]*>/i.test(lineStr)) {
    const html = markdownInlineToHtml(lineStr);
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  }

  // Split on **bold** and _italic_
  const parts = lineStr.split(/(\*\*.*?\*\*|_.*?_)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('_') && part.endsWith('_')) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
};

export const renderPlainTextWithFormatting = (text) => {
  if (!text) return null;

  const textStr = String(text).trim();

  // If there are no newlines, just return the parsed inline markdown directly (avoid wrapping in block elements like <p>)
  if (!textStr.includes('\n')) {
    return parseInlineMarkdown(textStr);
  }

  const lines = textStr.split('\n');
  const renderedElements = [];
  let inList = false;
  let listItems = [];

  const flushList = (key) => {
    if (listItems.length > 0) {
      renderedElements.push(
        <ul key={key} style={{ margin: '8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>
          {listItems.map((item, idx) => (
            <li key={idx} style={{ margin: '4px 0' }}>{item}</li>
          ))}
        </ul>
      );
      listItems = [];
      inList = false;
    }
  };

  lines.forEach((line, idx) => {
    const trimmedLine = line.trim();
    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('• ')) {
      inList = true;
      listItems.push(parseInlineMarkdown(trimmedLine.slice(2)));
    } else if (trimmedLine === '') {
      flushList(`list-${idx}`);
      // Add a paragraph break spacer or newline
      renderedElements.push(<div key={`spacer-${idx}`} style={{ height: '8px' }} />);
    } else {
      flushList(`list-${idx}`);
      renderedElements.push(
        <p key={`p-${idx}`} style={{ margin: '6px 0', lineHeight: '1.5' }}>
          {parseInlineMarkdown(line)}
        </p>
      );
    }
  });

  flushList('list-end');
  return <>{renderedElements}</>;
};
